import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  CircularProgress 
} from '@mui/material';
import { fetchUserProfile } from '../slices/authSlice';

const PrivateRoute = ({ children, roles = [] }) => {
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const fetchingRef = useRef(false);
  const lastFetchTime = useRef(0);

  useEffect(() => {
    // Prevent multiple fetches
    const now = Date.now();
    if (token && (!user?.role || !user?.id) && !fetchingRef.current && (now - lastFetchTime.current > 5000)) {
      fetchingRef.current = true;
      lastFetchTime.current = now;
      
      dispatch(fetchUserProfile()).finally(() => {
        fetchingRef.current = false;
      });
    }
  }, [dispatch, token, user]);

  // Add detailed logging
  console.log('PrivateRoute Check:', {
    hasToken: !!token,
    userObj: user,
    userRole: user?.role,
    allowedRoles: roles,
    path: window.location.pathname,
    hasAccess: !roles.length || roles.includes(user?.role),
    isFetching: fetchingRef.current
  });

  // If no token, redirect to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while fetching profile (but only once)
  if (!user?.role && token && fetchingRef.current) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Always allow superadmin
  if (user?.role === 'superadmin') {
    return children;
  }

  // If no roles specified, or user's role is in the allowed roles, render children
  if (!roles.length || roles.includes(user?.role)) {
    return children;
  }

  // For SCORM routes, allow access if user exists
  if (window.location.pathname.includes('/courses/') && window.location.pathname.includes('/lessons/') && user?.id) {
    console.log('Allowing SCORM access for authenticated user');
    return children;
  }

  // For unauthorized access, redirect to unauthorized page
  console.error('Access denied:', {
    userRole: user?.role,
    requiredRoles: roles,
    path: window.location.pathname
  });
  
  return <Navigate to="/unauthorized" replace />;
};

export default PrivateRoute;